import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { combineLatest, from, Observable, of } from 'rxjs';
import { catchError, filter, map, switchMap, take } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';
import { UpdateSimXUserDto } from '../../../shared/models/dtos/update-simx-user.dto';
import { SimXAgreement } from '../../../shared/models/entities/simx-agreement.model';
import { SimXInstitutionUser } from '../../../shared/models/entities/simx-institution-user.model';
import { SimXReleaseTag } from '../../../shared/models/entities/simx-release-tag.model';
import { SimXUser } from '../../../shared/models/entities/simx-user.model';
import { CreateSimXUserAgreementRequest } from '../../../shared/models/requests/create-simx-user-agreement-request.model';
import { LoginRequest } from '../../../shared/models/requests/login-request.model';
import { ResetSimXUserPasswordRequest } from '../../../shared/models/requests/reset-simx-user-password-request.model';
import { UpdateSimXUserPasswordRequest } from '../../../shared/models/requests/update-simx-user-password-request.model';
import { UpdateSimXUserRequest } from '../../../shared/models/requests/update-simx-user-request.model';
import { LoginResponse } from '../../../shared/models/responses/login-response.model';
import { AgreementDataService } from '../../../shared/services/agreement-data.service';
import { DataService } from '../../../shared/services/data.service';
import { UserAgreementDataService } from '../../../shared/services/user-agreement-data.service';
import { UserDataService } from '../../../shared/services/user-data.service';
import * as AuthenticationStoreActions from '../store/authentication-store.actions';
import * as AuthenticationStoreSelectors from '../store/authentication-store.selectors';

@Injectable({ providedIn: 'root' })
export class AuthenticationService {
  private readonly _actions$ = inject(Actions);
  private readonly _agreementDataService = inject(AgreementDataService);
  private readonly _dataService = inject(DataService);
  private readonly _http = inject(HttpClient);
  private readonly _router = inject(Router);
  private readonly _store = inject(Store);
  private readonly _userAgreementDataService = inject(UserAgreementDataService);
  private readonly _userDataService = inject(UserDataService);

  getIsLoggingIn$(): Observable<boolean> {
    return new Observable<boolean>(observer => {
      const subscription = this._store
        .select(AuthenticationStoreSelectors.selectIsLoggingIn)
        .subscribe(state => {
          observer.next(state);
        });

      return () => subscription.unsubscribe();
    });
  }

  getIsLoggedIn$(): Observable<boolean> {
    return new Observable<boolean>(observer => {
      const subscription = this._store
        .select(AuthenticationStoreSelectors.selectIsLoggedIn)
        .subscribe(state => {
          observer.next(state);
        });

      return () => subscription.unsubscribe();
    });
  }

  getLoginSucceeded$(): Observable<void> {
    return new Observable<void>(observer => {
      const subscription = this._actions$
        .pipe(ofType(AuthenticationStoreActions.ActionType.LoginSucceeded))
        .subscribe(() => {
          observer.next();
          observer.complete();
        });

      return () => subscription.unsubscribe();
    });
  }

  getLoginFailed$(): Observable<string> {
    return new Observable<string>(observer => {
      const subscription = this._actions$
        .pipe(ofType(AuthenticationStoreActions.ActionType.LoginFailed))
        .subscribe(({ error }) => {
          observer.next(error);
        });

      return () => subscription.unsubscribe();
    });
  }

  getUser$(): Observable<SimXUser | null> {
    return new Observable(observer => {
      const subscription = this._store
        .select(AuthenticationStoreSelectors.selectUser)
        .subscribe(user => {
          observer.next(user);
        });

      return () => subscription.unsubscribe();
    });
  }

  getUserId$(): Observable<string> {
    return new Observable(observer => {
      const subscription = this._store
        .select(AuthenticationStoreSelectors.selectUserId)
        .subscribe(id => {
          observer.next(id);
        });

      return () => subscription.unsubscribe();
    });
  }

  getUserName$(): Observable<string> {
    return new Observable(observer => {
      const subscription = this._store
        .select(AuthenticationStoreSelectors.selectUserName)
        .subscribe(name => {
          observer.next(name);
        });

      return () => subscription.unsubscribe();
    });
  }

  getUserInstitutions$(): Observable<Array<SimXInstitutionUser>> {
    return new Observable(observer => {
      const subscription = this._store
        .select(AuthenticationStoreSelectors.selectUserInstitutions)
        .subscribe(institutions => {
          observer.next(institutions);
        });

      return () => subscription.unsubscribe();
    });
  }

  getUserAllowedReleaseTagsForAnyInstitution$(): Observable<
    Array<SimXReleaseTag>
  > {
    return new Observable(observer => {
      const subscription = this._store
        .select(AuthenticationStoreSelectors.selectUserInstitutions)
        .subscribe(institutionUsers => {
          const releaseTagSet = new Map<string, SimXReleaseTag>();
          for (const institutionUser of institutionUsers) {
            if (
              Array.isArray(institutionUser.institution?.institutionReleaseTags)
            ) {
              for (const institutionReleaseTag of institutionUser.institution
                .institutionReleaseTags) {
                const { releaseTag } = institutionReleaseTag;
                if (
                  releaseTag &&
                  !releaseTagSet.has(institutionReleaseTag.releaseTag.id)
                ) {
                  releaseTagSet.set(releaseTag.id, releaseTag);
                }
              }
            }
          }
          observer.next(Array.from(releaseTagSet.values()));
        });

      return () => subscription.unsubscribe();
    });
  }

  getUserIsInternalUser$(): Observable<boolean> {
    return new Observable(observer => {
      const subscription = this.getUserInstitutions$().subscribe(
        institutions => {
          observer.next(
            !!institutions.find(institutionUser =>
              environment.internalInstitutions.includes(
                institutionUser.institution.id,
              ),
            ),
          );
        },
      );

      return () => subscription.unsubscribe();
    });
  }

  getToken$(): Observable<string> {
    return new Observable(observer => {
      const subscription = this._store
        .select(AuthenticationStoreSelectors.selectToken)
        .subscribe(token => {
          observer.next(token);
        });

      return () => subscription.unsubscribe();
    });
  }

  getIsAuthenticating$(): Observable<boolean> {
    return new Observable(observer => {
      const subscription = this._store
        .select(AuthenticationStoreSelectors.selectIsAuthenticating)
        .subscribe(state => {
          observer.next(state);
        });

      return () => subscription.unsubscribe();
    });
  }

  getIsAuthenticated$(): Observable<boolean> {
    return new Observable(observer => {
      const subscription = this._store
        .select(AuthenticationStoreSelectors.selectIsAuthenticated)
        .subscribe(state => {
          observer.next(state);
        });

      return () => subscription.unsubscribe();
    });
  }

  getUnsignedAgreements$(): Observable<Array<SimXAgreement>> {
    return new Observable(observer => {
      const subscription = this._store
        .select(AuthenticationStoreSelectors.selectUnsignedAgreements)
        .subscribe(unsignedAgreements => {
          observer.next(unsignedAgreements);
        });

      return () => subscription.unsubscribe();
    });
  }

  getIsSigningAgreement$(): Observable<boolean> {
    return new Observable(observer => {
      const subscription = this._store
        .select(AuthenticationStoreSelectors.selectIsAcceptingAgreement)
        .subscribe(state => {
          observer.next(state);
        });

      return () => subscription.unsubscribe();
    });
  }

  getSignAgreementSucceeded$(): Observable<void> {
    return new Observable(observer => {
      const subscription = this._actions$
        .pipe(
          ofType(AuthenticationStoreActions.ActionType.SignAgreementSucceeded),
        )
        .subscribe(() => {
          observer.next();
        });

      return () => subscription.unsubscribe();
    });
  }

  getSignAgreementFailed$(): Observable<string> {
    return new Observable(observer => {
      const subscription = this._actions$
        .pipe(ofType(AuthenticationStoreActions.ActionType.SignAgreementFailed))
        .subscribe(({ error }) => {
          observer.next(error);
        });

      return () => subscription.unsubscribe();
    });
  }

  requestLogin(data: LoginRequest): void {
    this._store.dispatch(AuthenticationStoreActions.requestLogin({ data }));
  }

  authenticateUser(
    data: LoginRequest,
  ): Observable<
    | Action<AuthenticationStoreActions.ActionType.AuthenticationSucceeded>
    | Action<AuthenticationStoreActions.ActionType.AuthenticationFailed>
  > {
    const url = this._dataService.loginPortalUrl();
    return this._http.post<LoginResponse>(url, data).pipe(
      map(data => AuthenticationStoreActions.authenticationSucceeded({ data })),
      catchError((error: HttpErrorResponse) => {
        let errorMessage: string;
        switch (error.status) {
          case 0:
            errorMessage = 'Service unavailable';
            break;
          case 401:
            errorMessage = 'Invalid user name or password';
            break;
          default:
            errorMessage = 'Unknown error occurred';
        }
        return of(
          AuthenticationStoreActions.authenticationFailed({
            error: errorMessage,
          }),
        );
      }),
    );
  }

  checkForUnsignedAgreements(): Observable<
    | Action<AuthenticationStoreActions.ActionType.UnsignedAgreementsCheckSucceeded>
    | Action<AuthenticationStoreActions.ActionType.UnsignedAgreementsCheckFailed>
  > {
    return combineLatest([this.getUser$(), this.getToken$()]).pipe(
      filter(([user, token]) => user !== null && token !== null),
      take(1),
      switchMap(([user]) =>
        from(
          this._agreementDataService.getRequiredAndUnsignedAgreementsForUser(
            user.id,
          ),
        ),
      ),
      map(unsignedAgreements =>
        AuthenticationStoreActions.unsignedAgreementsCheckSucceeded({
          data: unsignedAgreements,
        }),
      ),
      catchError((error: HttpErrorResponse) =>
        of(
          AuthenticationStoreActions.unsignedAgreementsCheckFailed({
            error: error.message,
          }),
        ),
      ),
    );
  }

  processUnsignedAgreements(unsignedAgreements: Array<SimXAgreement>): void {
    if (unsignedAgreements.length) {
      this._router.navigate(['/agreement']);
    } else {
      this._store.dispatch(AuthenticationStoreActions.loginSucceeded());
    }
  }

  requestSignAgreement(agreementId: string): void {
    this._store.dispatch(
      AuthenticationStoreActions.requestSignAgreement({ agreementId }),
    );
  }

  signAgreement(
    userId: string,
    agreementId: string,
  ): Observable<
    | Action<AuthenticationStoreActions.ActionType.SignAgreementSucceeded>
    | Action<AuthenticationStoreActions.ActionType.SignAgreementFailed>
  > {
    const request: CreateSimXUserAgreementRequest = {
      data: { agreement: agreementId, user: userId },
      returnOptions: { include: { agreement: {} } },
    };
    return from(
      this._userAgreementDataService.createUserAgreement(request),
    ).pipe(
      map(userAgreement =>
        AuthenticationStoreActions.signAgreementSucceeded({
          data: userAgreement,
        }),
      ),
      catchError((error: HttpErrorResponse) =>
        of(
          AuthenticationStoreActions.signAgreementFailed({
            error: error.message,
          }),
        ),
      ),
    );
  }

  completeLogin(): void {
    this._router.navigate(['/']);
  }

  requestLogout(): void {
    this._store.dispatch(AuthenticationStoreActions.requestLogout());
  }

  completeLogout(): void {
    this._router.navigate(['/login']);
  }

  validateToken(): Observable<void> {
    const url = this._dataService.validateTokenUrl();
    return this._http.get<void>(url);
  }

  updateUser(data: UpdateSimXUserDto): Observable<void> {
    return new Observable(observer => {
      const request: UpdateSimXUserRequest = { data };
      this._userDataService.updateUser(request.data.id, request).subscribe({
        next: response => {
          this.getUser$()
            .pipe(take(1))
            .subscribe(existingUser => {
              const user: SimXUser = {
                ...existingUser,
                ...response,
              };
              this._store.dispatch(
                AuthenticationStoreActions.setUser({ user }),
              );
              observer.next();
              observer.complete();
            });
        },
        error: error => {
          observer.error(error);
        },
      });
    });
  }

  updateUserPassword(request: UpdateSimXUserPasswordRequest): Observable<void> {
    return new Observable(observer => {
      this._userDataService
        .updateUserPassword(request.data.userId, request)
        .subscribe({
          next: () => {
            observer.next();
            observer.complete();
          },
          error: error => {
            observer.error(error);
          },
        });
    });
  }

  resetUserPassword(request: ResetSimXUserPasswordRequest): Observable<void> {
    return new Observable(observer => {
      this._userDataService.resetUserPassword(request).subscribe({
        next: () => {
          observer.next();
          observer.complete();
        },
        error: error => {
          observer.error(error);
        },
      });
    });
  }
}
